import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {loadPublicDeck, loadDeckSlides} from '../lib/tycho';
import events from '../lib/events';
import DeckDock from './DeckDock.jsx';
import PresentationWrapper from '../containers/PresentationWrapper';
import ScrollWrapper from '../containers/ScrollWrapper';
import './Deck.css';

function buildDeckUrl(folderId, deckId) {
  return new URL(`/folders/${folderId}/decks/${deckId}/slides`, window.env.ADMIN_URL).href;
}

const Deck = ({
  id,
  invite,
  match,
  name,
  loadSubData = false,
  slideIndex = null,
  slide_order = [],
  slideReferrer,
  canEditFolder = false,
  folder_id,
  onOtpFailure,
  uiMode,
  options = {},
  theme,
  toggleInfoConsole,
  focusMode
}) => {
  const [isLoading, setIsLoading] = useState(true);

  const slideCount = slide_order.length;
  const deckHash = match ? match.params.deckHash : null;

  useEffect(() => {
    if (deckHash) {
      loadPublicDeck(deckHash);
    }
  }, [deckHash]);

  // Loads the sub-data relating to this deck (slides, components etc), when
  // the corresponding flag has been set.
  useEffect(() => {
    let hasBeenCleanedUp = false;

    if (loadSubData && id) {
      loadDeckSlides(id)
        .then(() => {
          if (!hasBeenCleanedUp) {
            setIsLoading(false);
          }
        })
        .catch(err => {
          // This is used for the private link flow to signal that loading the
          // deck has failed, which probably means a token couldn't be obtained
          // from the OTP.
          if (onOtpFailure) {
            onOtpFailure();
          }
        });

      events.listen({deckId: id});
    }
    return function unlisten() {
      hasBeenCleanedUp = true;
      if (loadSubData) {
        events.unlisten({deckId: id});
      }
    };
  }, [loadSubData, id]);

  return (
    <div
      className={classNames('Deck', {
        [`Deck--${uiMode}`]: true,
        'Deck--loading': slideCount === 0,
        'Deck--focusMode': focusMode
      })}
      data-testid={`deck-${id}`}
    >
      <div className="Deck__body">
        {uiMode === 'scroll' ? (
          <ScrollWrapper
            id={id}
            name={name}
            slideReferrer={slideReferrer}
            slideIndex={slideIndex}
          />
        ) : (
          <PresentationWrapper
            id={id}
            slideIndex={slideIndex}
          />
        )}
      </div>
      <div className="Deck__dock">
        <DeckDock
          invite={invite}
          name={name}
          options={options}
          slideCount={slideCount}
          slideIndex={slideIndex}
          canEditFolder={canEditFolder}
          deckEditUrl={buildDeckUrl(folder_id, id)}
          uiMode={uiMode}
          toggleInfoConsole={toggleInfoConsole}
          theme={theme}
        />
      </div>
      <div className="Deck__focusMask" />
    </div>
  );
};

Deck.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  invite: PropTypes.object,
  match: PropTypes.object,
  name: PropTypes.string,
  options: PropTypes.object,
  slideIndex: PropTypes.number,
  slideReferrer: PropTypes.string,
  slide_order: PropTypes.array,
  loadSubData: PropTypes.bool, // should it load its own slides, components, etc.
  canEditFolder: PropTypes.bool,
  folder_id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  onOtpFailure: PropTypes.func,
  theme: PropTypes.object,
  uiMode: PropTypes.string.isRequired,
  focusMode: PropTypes.bool
};

export default Deck;
